import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../lib/api';
import { applySystemTheme } from '../lib/systemThemeCss';
import type { SystemConfig } from '../types';

type SystemConfigContextValue = {
  config: SystemConfig | null;
  loading: boolean;
  error: string | null;
  reloadConfig: () => Promise<boolean>;
  refresh: () => Promise<void>;
};

const SystemConfigContext = createContext<SystemConfigContextValue | null>(null);

export function SystemConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<SystemConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getSystemConfig();
      setConfig(data);
      setError(null);
      applySystemTheme(data);
    } catch (err) {
      console.error('[SystemConfig] Failed to fetch:', err);
      setError(err instanceof Error ? err.message : 'Failed to load system config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  const reloadConfig = useCallback(async () => {
    try {
      await api.reloadSystemConfig();
      await fetchConfig();
      return true;
    } catch (err) {
      console.error('[SystemConfig] Reload failed:', err);
      return false;
    }
  }, [fetchConfig]);

  const value = useMemo<SystemConfigContextValue>(
    () => ({
      config,
      loading,
      error,
      reloadConfig,
      refresh: fetchConfig,
    }),
    [config, loading, error, reloadConfig, fetchConfig],
  );

  return (
    <SystemConfigContext.Provider value={value}>{children}</SystemConfigContext.Provider>
  );
}

export function useSystemConfig() {
  const ctx = useContext(SystemConfigContext);
  if (!ctx) {
    throw new Error('useSystemConfig must be used within SystemConfigProvider');
  }
  return ctx;
}
